// Live IP/VLAN consistency engine for the Design-Check panel.
//
// Pure functions: given the document, return a flat list of findings. Each
// finding carries a translation `code` + `params` (the UI renders the message
// via i18n) and `targets` (devices / vlans) for click-to-focus.
//
// Severity policy (per product spec):
//   - error   : duplicate IPs, invalid IPs, IP outside the device VLAN subnet,
//               duplicate VLAN IDs
//   - warning : overlapping subnets, invalid/overlapping DHCP ranges
//   - info    : devices without a VLAN, unconnected ports (unconnected ports
//               are NORMAL on switches → info only, hidden by default)
import { ipToInt, isValidIPv4, subnetInfo, ipInSubnet, dhcpValid } from './subnet';
import { deviceLabel } from './deviceIcons';
import { getAllEdges } from './detail';
import { resolvePortVlans } from './vlanPorts';

export const SEVERITY = { ERROR: 'error', WARNING: 'warning', INFO: 'info' };

function deviceName(node, lang) {
  return node?.data?.label || deviceLabel(node?.data?.deviceType, lang);
}

function allDevices(doc) {
  const list = [];
  (doc.pages || []).forEach((p) => {
    (p.nodes || []).forEach((n) => {
      if (n.data?.aliasOf) return; // master devices only — no double-count
      list.push({ id: n.id, node: n, pageId: p.id });
    });
  });
  return list;
}

function rangesOverlap(aLo, aHi, bLo, bHi) {
  return aLo <= bHi && bLo <= aHi;
}

// Returns { findings: [...], counts: { error, warning, info } }
export function runDesignChecks(doc, options = {}) {
  const { includeInfo = true, includeUnconnected = false, lang = 'de' } = options;
  const findings = [];
  const devices = allDevices(doc);
  const vlans = doc.vlans || [];
  const vlanById = {};
  vlans.forEach((v) => (vlanById[v.id] = v));

  const push = (severity, code, params, targets) => findings.push({ id: `${code}:${(targets || []).map((t) => t.id).join('-') || findings.length}`, severity, code, params: params || {}, targets: targets || [] });

  // ---- Collect ALL IPs: device management IP + per-port IPs + virtual
  // interface IPs. Each entry knows the VLAN it should belong to (device VLAN,
  // the port's untagged VLAN, or the interface's untagged VLAN) so the subnet
  // check works for multi-IP devices (firewalls, L3 switches, hosts).
  const resolvedCache = {};
  const resolveFor = (d) => {
    if (!resolvedCache[d.id]) resolvedCache[d.id] = resolvePortVlans(d.node, doc);
    return resolvedCache[d.id];
  };
  const ipEntries = [];
  devices.forEach((d) => {
    const dname = deviceName(d.node, lang);
    const add = (ip, vlanRef, srcLabel) => {
      const v = (ip || '').trim();
      if (!v) return;
      ipEntries.push({ ip: v, vlanRef: vlanRef || null, deviceId: d.id, src: srcLabel });
    };
    add(d.node.data?.ip, d.node.data?.vlanRef, dname);
    const resolved = resolveFor(d);
    (d.node.data?.ports || []).forEach((p) => {
      if (!(p.ip || '').trim()) return;
      const u = resolved[p.id]?.untaggedVlan;
      add(p.ip, u ? u.id : null, `${dname} \u00b7 ${p.name}`);
    });
    (d.node.data?.interfaces || []).forEach((it) => {
      const iname = `${dname} \u00b7 ${it.name || ''}`.trim();
      if ((it.ip || '').trim()) add(it.ip, it.untaggedVlanRef || null, iname);
      // Per-tagged-VLAN IPs: each is validated against its own tagged VLAN.
      const tips = it.taggedIps || {};
      Object.keys(tips).forEach((vref) => {
        if ((tips[vref] || '').trim()) add(tips[vref], vref || null, iname);
      });
    });
  });

  // ---- Duplicate / invalid IPs --------------------------------------------
  const byIp = new Map();
  ipEntries.forEach((e) => {
    if (!isValidIPv4(e.ip)) {
      push(SEVERITY.ERROR, 'ip_invalid', { ip: e.ip, name: e.src }, [{ type: 'device', id: e.deviceId }]);
      return;
    }
    if (!byIp.has(e.ip)) byIp.set(e.ip, []);
    byIp.get(e.ip).push(e);
  });
  byIp.forEach((list, ip) => {
    // Dedupe device targets so a single device with the same IP on two adapters
    // isn't reported as a "duplicate"; only flag when 2+ distinct devices clash.
    const distinct = Array.from(new Set(list.map((e) => e.deviceId)));
    if (distinct.length >= 2) {
      push(SEVERITY.ERROR, 'duplicate_ip', { ip, count: distinct.length }, distinct.map((id) => ({ type: 'device', id })));
    }
  });

  // ---- IP outside its VLAN subnet -----------------------------------------
  ipEntries.forEach((e) => {
    if (!isValidIPv4(e.ip) || !e.vlanRef) return;
    const v = vlanById[e.vlanRef];
    if (!v || !v.network) return;
    const info = subnetInfo(v.network);
    if (!info.valid) return;
    if (!ipInSubnet(e.ip, v.network)) {
      push(SEVERITY.ERROR, 'ip_not_in_subnet', { ip: e.ip, network: v.network, vlanId: v.vlanId }, [{ type: 'device', id: e.deviceId }]);
    }
  });

  // ---- Untagged VLAN mismatch between connected adapters -------------------
  // A connection where BOTH endpoint ports carry an untagged/access VLAN that
  // differs is almost always a misconfiguration (the access VLANs won't talk).
  const nodeById = {};
  devices.forEach((d) => (nodeById[d.id] = d));
  // Map alias placement ids onto their master device entry so connections drawn
  // on a clone's page are still checked (and not double-counted as devices).
  (doc.pages || []).forEach((p) =>
    (p.nodes || []).forEach((n) => {
      if (n.data?.aliasOf && nodeById[n.data.aliasOf]) nodeById[n.id] = nodeById[n.data.aliasOf];
    })
  );
  getAllEdges(doc).forEach((e) => {
    if (!e.sourcePortId || !e.targetPortId) return;
    const sd = nodeById[e.source];
    const td = nodeById[e.target];
    if (!sd || !td) return;
    const su = resolveFor(sd)[e.sourcePortId]?.untaggedVlan;
    const tu = resolveFor(td)[e.targetPortId]?.untaggedVlan;
    if (su && tu && su.vlanId !== tu.vlanId) {
      push(
        SEVERITY.WARNING,
        'untagged_vlan_mismatch',
        { a: deviceName(sd.node, lang), va: su.vlanId, b: deviceName(td.node, lang), vb: tu.vlanId },
        [{ type: 'device', id: e.source }, { type: 'device', id: e.target }]
      );
    }
  });

  // ---- Duplicate VLAN IDs --------------------------------------------------
  // When zones are enabled, the same VLAN ID may legitimately exist in
  // different zones. We therefore key the uniqueness check by (zone + id).
  // With zones disabled the key is just the id (global uniqueness as before).
  const zonesEnabled = !!doc.zonesEnabled;
  const zoneById = {};
  (doc.zones || []).forEach((z) => (zoneById[z.id] = z));
  const byVlanId = new Map();
  vlans.forEach((v) => {
    const key = zonesEnabled ? `${v.zoneId || ''}::${v.vlanId}` : String(v.vlanId);
    if (!byVlanId.has(key)) byVlanId.set(key, []);
    byVlanId.get(key).push(v);
  });
  byVlanId.forEach((list) => {
    if (list.length >= 2) {
      const zName = zonesEnabled ? (zoneById[list[0].zoneId]?.name || null) : null;
      push(SEVERITY.ERROR, 'duplicate_vlan_id', { vlanId: String(list[0].vlanId), count: list.length, zone: zName }, list.map((v) => ({ type: 'vlan', id: v.id })));
    }
  });

  // ---- Overlapping subnets -------------------------------------------------
  const valid = vlans
    .map((v) => ({ v, info: v.network ? subnetInfo(v.network) : null }))
    .filter((x) => x.info && x.info.valid);
  for (let i = 0; i < valid.length; i++) {
    for (let j = i + 1; j < valid.length; j++) {
      const a = valid[i];
      const b = valid[j];
      const aLo = ipToInt(a.info.network);
      const aHi = ipToInt(a.info.broadcast);
      const bLo = ipToInt(b.info.network);
      const bHi = ipToInt(b.info.broadcast);
      if (rangesOverlap(aLo, aHi, bLo, bHi)) {
        push(
          SEVERITY.WARNING,
          'subnet_overlap',
          { a: `VLAN ${a.v.vlanId} (${a.v.network})`, b: `VLAN ${b.v.vlanId} (${b.v.network})` },
          [{ type: 'vlan', id: a.v.id }, { type: 'vlan', id: b.v.id }]
        );
      }
    }
  }

  // ---- DHCP range invalid --------------------------------------------------
  const dhcpRanges = [];
  vlans.forEach((v) => {
    if (!v.network || !v.dhcpStart || !v.dhcpEnd) return;
    const res = dhcpValid(v.network, v.dhcpStart, v.dhcpEnd);
    if (!res.valid) {
      push(SEVERITY.WARNING, 'dhcp_invalid', { vlanId: v.vlanId }, [{ type: 'vlan', id: v.id }]);
    } else {
      dhcpRanges.push({ v, lo: ipToInt(v.dhcpStart), hi: ipToInt(v.dhcpEnd) });
    }
  });

  // ---- DHCP range overlap (across VLANs) ----------------------------------
  for (let i = 0; i < dhcpRanges.length; i++) {
    for (let j = i + 1; j < dhcpRanges.length; j++) {
      const a = dhcpRanges[i];
      const b = dhcpRanges[j];
      if (rangesOverlap(a.lo, a.hi, b.lo, b.hi)) {
        push(SEVERITY.WARNING, 'dhcp_overlap', { a: a.v.vlanId, b: b.v.vlanId }, [{ type: 'vlan', id: a.v.id }, { type: 'vlan', id: b.v.id }]);
      }
    }
  }

  // ---- Info: devices without a VLAN ---------------------------------------
  if (includeInfo) {
    devices.forEach((d) => {
      if (!d.node.data?.vlanRef) {
        push(SEVERITY.INFO, 'device_no_vlan', { name: deviceName(d.node, lang) }, [{ type: 'device', id: d.id }]);
      }
    });
  }

  // ---- Info: unconnected ports (off by default; normal on switches) -------
  if (includeInfo && includeUnconnected) {
    const used = new Set();
    getAllEdges(doc).forEach((e) => {
      if (e.sourcePortId) used.add(e.sourcePortId);
      if (e.targetPortId) used.add(e.targetPortId);
    });
    devices.forEach((d) => {
      const ports = d.node.data?.ports || [];
      const free = ports.filter((p) => !used.has(p.id)).length;
      if (ports.length && free > 0) {
        push(SEVERITY.INFO, 'unconnected_ports', { name: deviceName(d.node, lang), count: free }, [{ type: 'device', id: d.id }]);
      }
    });
  }

  const counts = { error: 0, warning: 0, info: 0 };
  findings.forEach((f) => (counts[f.severity] += 1));
  return { findings, counts };
}
